import { Link } from "react-router-dom";
import {
  ArrowRight, Play, Mic, ClipboardList, ShieldCheck, Users,
  Sparkles, Brain, HeartPulse, Lock, TrendingUp, Video,
  Package, BookOpen, Wind, Headphones, Clock, Radio,
} from "lucide-react";
import HeroLiveWaveform from "@/components/HeroLiveWaveform";
import SocialLinks from "@/components/SocialLinks";

const container = "max-w-6xl mx-auto px-6";

function Overline({ children, className = "" }) {
  return (
    <div
      className={`text-[11px] uppercase tracking-[0.22em] font-semibold text-[color:var(--mano-primary)] ${className}`}
    >
      {children}
    </div>
  );
}

function BrandBadge({ inverse = false }) {
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-3"
      data-testid="landing-brand"
    >
      <div className="leading-none">
        <div className={`font-display font-bold tracking-tight leading-none text-3xl sm:text-[32px] ${
          inverse ? "text-white" : "text-[color:var(--mano-primary)]"
        }`}>
          ManoV<span className={inverse ? "text-white/90" : "text-[color:var(--mano-ink)]"}>āk</span>
          <sup className={`text-[10px] font-semibold ml-0.5 align-top ${inverse ? "text-white/60" : "text-[color:var(--mano-ink-soft)]"}`}>™</sup>
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <span className={`text-[9px] font-semibold uppercase tracking-[0.24em] ${
            inverse ? "text-white/60" : "text-[color:var(--mano-ink-soft)]"
          }`}>
            by
          </span>
          <img
            src="/manodayam-wordmark.png"
            alt="Manodayam"
            className={`h-3.5 w-auto object-contain ${inverse ? "brightness-0 invert opacity-80" : "opacity-85"}`}
          />
        </div>
      </div>
    </Link>
  );
}

function PrimaryCta({ to, children, "data-testid": testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-full bg-[color:var(--mano-primary)] text-white px-6 py-3 font-semibold text-sm hover:bg-[color:var(--mano-primary-hover)] hover:-translate-y-0.5 transition-[transform,background-color] shadow-[0_10px_30px_-10px_rgba(30,123,255,0.55)]"
    >
      {children} <ArrowRight size={15} />
    </Link>
  );
}

function GhostCta({ to, children, icon: Icon = Play, "data-testid": testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-sm text-[color:var(--mano-ink)] hover:bg-[color:var(--mano-blue-light)] transition-colors"
    >
      <span className="h-8 w-8 rounded-full border border-[color:var(--mano-ink)] flex items-center justify-center">
        <Icon size={13} />
      </span>
      {children}
    </Link>
  );
}

/* --- Sections --- */

function Nav() {
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-[color:var(--mano-border)]">
      <div className={`${container} h-16 flex items-center justify-between`}>
        <BrandBadge />
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-[color:var(--mano-ink)]">
          <a href="#how" className="hover:text-[color:var(--mano-primary)]">How it works</a>
          <a href="#assessments" className="hover:text-[color:var(--mano-primary)]">Assessments</a>
          <a href="#progress" className="hover:text-[color:var(--mano-primary)]">Progress</a>
          <a href="#care" className="hover:text-[color:var(--mano-primary)]">Care</a>
          <a href="#experts" className="hover:text-[color:var(--mano-primary)]">Experts</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="landing-signin"
            className="hidden sm:inline-flex text-sm font-semibold text-[color:var(--mano-ink)] hover:text-[color:var(--mano-primary)]"
          >
            Sign in
          </Link>
          <PrimaryCta to="/register" data-testid="landing-cta-hero">Check in with ManoVāk</PrimaryCta>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className={`${container} pt-14 pb-24 grid lg:grid-cols-2 gap-14 items-center`}>
      <div>
        <Overline>ManoVāk · Voice-led wellbeing</Overline>
        <h1 className="mt-6 font-display text-[46px] sm:text-6xl lg:text-[78px] leading-[1.02] tracking-tight text-[color:var(--mano-ink)]">
          Understand your mind, starting with <span className="text-[color:var(--mano-primary)]">your voice.</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-[color:var(--mano-ink-soft)] max-w-xl leading-relaxed">
          A short guided voice check-in helps you explore your mental and cognitive
          wellbeing, understand changes over time, and discover the right support
          when you need it.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <PrimaryCta to="/register" data-testid="hero-start">Start my voice check-in</PrimaryCta>
          <GhostCta to="#how" data-testid="hero-see-how">See how it works</GhostCta>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-6 text-[13px] text-[color:var(--mano-ink-soft)]">
          <span className="inline-flex items-center gap-1.5"><Clock size={14} /> ~30 sec check-in</span>
          <span className="inline-flex items-center gap-1.5"><Lock size={14} /> Private &amp; secure</span>
          <span className="inline-flex items-center gap-1.5"><Radio size={14} /> No special equipment</span>
        </div>
      </div>
      <div className="relative">
        <div
          aria-hidden
          className="absolute -inset-16 rounded-[48px] bg-[color:var(--mano-blue-light)] opacity-70 -z-10 blur-2xl"
        />
        <HeroLiveWaveform />
      </div>
    </section>
  );
}

function ValueProps() {
  const items = [
    { icon: Mic, title: "Voice-led screening", body: "Guided voice check-ins designed around mental and cognitive wellbeing." },
    { icon: ClipboardList, title: "Standardized assessments", body: "Go deeper with established screening questionnaires and cognitive screens." },
    { icon: ShieldCheck, title: "Private by design", body: "Your voice and wellbeing information are handled with care and security." },
    { icon: Users, title: "Human support", body: "Professional help is available when you want the next step." },
  ];
  return (
    <section className={`${container} pb-24`}>
      <div className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-8 lg:p-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {items.map((it, i) => (
          <div key={i} data-testid={`vp-${i}`} className="space-y-3">
            <div className="h-11 w-11 rounded-2xl bg-[color:var(--mano-blue-light)] flex items-center justify-center text-[color:var(--mano-primary)]">
              <it.icon size={18} />
            </div>
            <h3 className="font-display text-lg text-[color:var(--mano-ink)]">{it.title}</h3>
            <p className="text-sm text-[color:var(--mano-ink-soft)] leading-relaxed">{it.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="bg-[color:var(--mano-surface)] py-24">
      <div className={container}>
        <div className="text-center max-w-3xl mx-auto">
          <Overline>How it works</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-[color:var(--mano-ink)] tracking-tight">
            From a few spoken words to a clearer next step.
          </h2>
          <p className="mt-5 text-[color:var(--mano-ink-soft)]">
            Check in, understand what your screening suggests, and decide what feels right for you.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-3 gap-6">
          {/* 01 Check in */}
          <div className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-7 flex flex-col gap-5">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[color:var(--mano-primary)]">01 / Check in</div>
            <h3 className="font-display text-2xl text-[color:var(--mano-ink)]">Speak naturally for a few moments.</h3>
            <p className="text-sm text-[color:var(--mano-ink-soft)] leading-relaxed">
              ManoVāk guides you through simple voice prompts. There is nothing to prepare and
              no need to find the &ldquo;right&rdquo; words.
            </p>
            <div className="mt-2 rounded-2xl bg-[color:var(--mano-blue-light)]/60 p-4">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--mano-ink-soft)]">Guided prompt</div>
              <div className="mt-1 text-sm text-[color:var(--mano-ink)]">How has your day felt so far?</div>
              <div className="mt-3 flex gap-[3px] h-10 items-center">
                {Array.from({ length: 28 }).map((_, i) => (
                  <span
                    key={i}
                    className="hero-wave-bar"
                    style={{
                      height: `${25 + (Math.sin(i * 0.7) + 1) * 35}%`,
                      animationDelay: `${(i * 55) % 900}ms`,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* 02 Understand */}
          <div className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-7 flex flex-col gap-5">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[color:var(--mano-primary)]">02 / Understand</div>
            <h3 className="font-display text-2xl text-[color:var(--mano-ink)]">Turn your check-in into something you can understand.</h3>
            <p className="text-sm text-[color:var(--mano-ink-soft)] leading-relaxed">
              See your screening results in a clear, approachable format — without overwhelming clinical language.
            </p>
            <div className="mt-2 rounded-2xl bg-white border border-[color:var(--mano-border)] p-4">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--mano-ink-soft)]">Mental wellbeing</div>
              <div className="text-[color:var(--mano-normal)] font-semibold mt-0.5">Within expected range</div>
              <span className="inline-flex items-center gap-1 mt-1 rounded-full bg-[color:var(--mano-normal)]/12 text-[color:var(--mano-normal)] px-2 py-0.5 text-[11px] font-bold">
                <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--mano-normal)]" /> Normal
              </span>
              <p className="mt-3 text-xs text-[color:var(--mano-ink-soft)] leading-relaxed">
                <Sparkles size={11} className="inline text-[color:var(--mano-primary)] mr-1" />
                Your response and voice patterns are currently within the expected range.
              </p>
            </div>
          </div>

          {/* 03 Next step */}
          <div className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-7 flex flex-col gap-5">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[color:var(--mano-primary)]">03 / Next step</div>
            <h3 className="font-display text-2xl text-[color:var(--mano-ink)]">Support should not stop at a score.</h3>
            <p className="text-sm text-[color:var(--mano-ink-soft)] leading-relaxed">
              Track changes over time, take deeper assessments, explore care options or connect
              with an expert when needed.
            </p>
            <div className="mt-2 rounded-2xl bg-white border border-[color:var(--mano-border)] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-widest font-semibold text-[color:var(--mano-ink-soft)]">Your progress</span>
                <TrendingUp size={13} className="text-[color:var(--mano-primary)]" />
              </div>
              <svg viewBox="0 0 200 40" className="w-full h-8">
                <path d="M0,30 C 30,25 45,15 70,20 S 110,35 140,25 S 175,10 200,15" fill="none" stroke="var(--mano-primary)" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
              <div className="flex items-center justify-between text-xs pt-1 border-t border-[color:var(--mano-border)]">
                <span className="text-[color:var(--mano-ink)] font-semibold">PHQ-9</span>
                <span className="text-[10px] font-bold rounded-full bg-[color:var(--mano-mild)]/16 text-[color:var(--mano-mild)] px-2 py-0.5">Mild</span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <div className="h-8 w-8 rounded-full bg-[color:var(--mano-ink)] text-white text-[10px] font-bold flex items-center justify-center">AM</div>
                <div>
                  <div className="text-xs font-semibold text-[color:var(--mano-ink)]">Dr. Ananya Mehta</div>
                  <div className="text-[10px] text-[color:var(--mano-ink-soft)]">Psychiatrist</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TwoWaysToCheckIn() {
  const cards = [
    {
      overline: "Mental wellbeing",
      title: "Check in with mood, stress and emotional wellbeing.",
      body: "Use guided voice prompts to explore how you have been feeling lately.",
      cta: "Check mental wellbeing",
      Icon: HeartPulse,
      testid: "way-mental",
    },
    {
      overline: "Cognitive wellbeing",
      title: "Explore memory, attention and cognitive functioning.",
      body: "Use short voice-led prompts designed around cognitive screening.",
      cta: "Check cognitive wellbeing",
      Icon: Brain,
      testid: "way-cognitive",
    },
  ];
  return (
    <section id="assessments" className={`${container} py-24`}>
      <div className="grid lg:grid-cols-2 gap-10 items-end">
        <div>
          <Overline>Two ways to check in</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-[color:var(--mano-ink)] tracking-tight">
            Your wellbeing is more than one score.
          </h2>
        </div>
        <p className="text-[color:var(--mano-ink-soft)] max-w-md">
          Choose what you want to explore today. Both experiences are designed to feel simple,
          supportive, and easy to repeat over time.
        </p>
      </div>
      <div className="mt-12 grid md:grid-cols-2 gap-6">
        {cards.map((c) => (
          <div
            key={c.testid}
            data-testid={c.testid}
            className="relative rounded-3xl border border-[color:var(--mano-border)] bg-gradient-to-br from-[color:var(--mano-blue-light)]/60 via-white to-white p-8 overflow-hidden hover:-translate-y-1 transition-transform"
          >
            <c.Icon size={110} className="absolute -bottom-6 -right-4 text-[color:var(--mano-primary)]/10" />
            <div className="h-12 w-12 rounded-2xl bg-white border border-[color:var(--mano-border)] flex items-center justify-center text-[color:var(--mano-primary)]">
              <c.Icon size={20} />
            </div>
            <div className="mt-6 text-[10px] uppercase tracking-[0.22em] font-semibold text-[color:var(--mano-primary)]">
              {c.overline}
            </div>
            <h3 className="mt-2 font-display text-2xl sm:text-3xl leading-tight text-[color:var(--mano-ink)]">
              {c.title}
            </h3>
            <p className="mt-3 text-sm text-[color:var(--mano-ink-soft)] leading-relaxed max-w-sm">
              {c.body}
            </p>
            <Link
              to="/register"
              className="mt-6 inline-flex items-center gap-1 font-semibold text-sm text-[color:var(--mano-primary)]"
            >
              {c.cta} <ArrowRight size={14} />
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}

function VoiceLab() {
  return (
    <section className="bg-[color:var(--mano-surface)] py-24">
      <div className={`${container} grid lg:grid-cols-2 gap-12 items-center`}>
        <div className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6 shadow-[0_25px_60px_-40px_rgba(10,39,69,0.35)]">
          <div className="flex items-center gap-1.5 text-[10px] text-[color:var(--mano-ink-soft)] pb-3 border-b border-[color:var(--mano-border)]">
            <span className="h-2 w-2 rounded-full bg-[#ff5f57]" />
            <span className="h-2 w-2 rounded-full bg-[#febc2e]" />
            <span className="h-2 w-2 rounded-full bg-[#28c840]" />
            <span className="ml-3 font-mono">manovak.app/lab</span>
          </div>
          <div className="pt-4">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--mano-primary)]">Voice Lab</div>
            <div className="font-display text-lg text-[color:var(--mano-ink)] mt-1">Standardized questionnaires</div>
            <div className="mt-4 space-y-2">
              {[
                { name: "MoCA-lite (Cognitive Screen)", meta: "Score 10", band: "Normal", color: "normal" },
                { name: "PHQ-9 (Depression)", meta: "Score 9", band: "Mild", color: "mild" },
                { name: "GAD-7 (Anxiety)", meta: "Score 6", band: "Mild", color: "mild" },
              ].map((r) => (
                <div key={r.name} className="flex items-center justify-between rounded-xl bg-[color:var(--mano-blue-light)]/50 px-3 py-2.5 text-sm">
                  <div>
                    <div className="font-semibold text-[color:var(--mano-ink)]">{r.name}</div>
                    <div className="text-[11px] text-[color:var(--mano-ink-soft)]">{r.meta}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-[color:var(--mano-${r.color})]/15 text-[color:var(--mano-${r.color})]`}>
                    {r.band}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <Overline>Voice Lab</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-[color:var(--mano-ink)]">
            Go deeper when you want to.
          </h2>
          <p className="mt-5 text-[color:var(--mano-ink-soft)] max-w-md">
            Complement your voice check-ins with standardized questionnaires and cognitive
            screens for a broader view of your wellbeing.
          </p>
          <div className="mt-6 space-y-2">
            {[
              { code: "PHQ-9", label: "Depression screening" },
              { code: "GAD-7", label: "Anxiety screening" },
              { code: "MoCA-lite", label: "Cognitive screening" },
            ].map((r) => (
              <div key={r.code} className="flex items-center justify-between rounded-2xl bg-white border border-[color:var(--mano-border)] px-4 py-3">
                <span className="font-semibold text-[color:var(--mano-ink)]">{r.code}</span>
                <span className="text-sm text-[color:var(--mano-ink-soft)]">{r.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <PrimaryCta to="/register" data-testid="voicelab-cta">Explore Voice Lab</PrimaryCta>
          </div>
        </div>
      </div>
    </section>
  );
}

function Journey() {
  return (
    <section id="progress" className="bg-[color:var(--mano-hero)] py-24 text-white">
      <div className={`${container} grid lg:grid-cols-2 gap-12 items-center`}>
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white/60">Your journey</div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight tracking-tight">
            A single check-in is a moment.
            <span className="block text-white/70">Patterns tell the story.</span>
          </h2>
          <p className="mt-5 text-white/70 max-w-md">
            Come back over time to understand how your mental and cognitive wellbeing is changing —
            rather than treating every assessment in isolation.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-white/85">
            {["See previous check-ins", "Understand patterns over time", "Keep your care journey connected"].map((s) => (
              <li key={s} className="flex items-center gap-2">
                <span className="h-5 w-5 rounded-full bg-white/12 text-white flex items-center justify-center text-[10px]">✓</span>
                {s}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-3xl bg-white/8 border border-white/15 p-6 backdrop-blur">
          <div className="flex items-center justify-between text-[11px] text-white/70">
            <span className="uppercase tracking-widest font-semibold">Longitudinal view</span>
            <button className="inline-flex items-center gap-1 rounded-full bg-white/12 hover:bg-white/20 px-3 py-1 text-white font-semibold transition-colors">
              Export
            </button>
          </div>
          <div className="mt-2 font-display text-xl">Your wellbeing over time</div>
          <div className="mt-4 flex items-center gap-4 text-[11px] text-white/70">
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[color:var(--mano-primary)]" /> Mental wellbeing</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-white/60" /> Cognitive</span>
          </div>
          <svg viewBox="0 0 400 150" className="mt-5 w-full">
            <line x1="0" y1="40" x2="400" y2="40" stroke="white" strokeOpacity="0.12" strokeDasharray="4 6" />
            <line x1="0" y1="110" x2="400" y2="110" stroke="white" strokeOpacity="0.12" strokeDasharray="4 6" />
            <path d="M0,90 C 50,70 90,100 140,80 S 240,60 290,70 S 360,50 400,55"
                  fill="none" stroke="var(--mano-primary)" strokeWidth="3" strokeLinecap="round" />
            <path d="M0,100 C 50,110 90,90 140,95 S 240,105 290,100 S 360,90 400,88"
                  fill="none" stroke="white" strokeOpacity="0.55" strokeWidth="2" strokeLinecap="round" strokeDasharray="3 4" />
            {[[60,86],[140,82],[220,75],[290,72],[360,60]].map(([x,y], i) => (
              <circle key={i} cx={x} cy={y} r="4" fill="var(--mano-primary)" stroke="white" strokeWidth="1.5" />
            ))}
          </svg>
          <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/60">
            <span>Higher concern</span><span>Expected</span><span>Lower</span>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3 pt-4 border-t border-white/12 text-sm">
            <div>
              <div className="text-[10px] text-white/60 uppercase tracking-widest">Latest</div>
              <div className="font-semibold">Within expected range</div>
            </div>
            <div>
              <div className="text-[10px] text-white/60 uppercase tracking-widest">Check-ins</div>
              <div className="font-semibold">12</div>
            </div>
            <div>
              <div className="text-[10px] text-white/60 uppercase tracking-widest">Last updated</div>
              <div className="font-semibold">2 days ago</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BeyondScreening() {
  const cards = [
    { pill: "Live", Icon: Video, title: "Talk to an expert", body: "Connect with verified psychiatrists, psychologists and cognitive-care professionals.", cta: "Explore" },
    { pill: "Live", Icon: Package, title: "Guided care", body: "Structured wellbeing and care programmes that keep your next steps connected.", cta: "Explore" },
    { pill: "Coming soon", Icon: BookOpen, title: "Short courses", body: "Practical learning journeys you can complete at your own pace.", cta: "Learn more" },
    { pill: "Coming soon", Icon: Wind, title: "Adjunctive therapies", body: "Explore supportive approaches that can complement your wider care plan.", cta: "Learn more" },
    { pill: "Coming soon", Icon: Brain, title: "Cognitive activities", body: "Simple activities designed to support ongoing cognitive wellbeing.", cta: "Learn more" },
    { pill: "Coming soon", Icon: Headphones, title: "Mindfulness & audio", body: "Guided audio experiences for reflection, relaxation and routines.", cta: "Learn more" },
  ];
  return (
    <section id="care" className={`${container} py-24`}>
      <div className="max-w-3xl">
        <Overline>Beyond screening</Overline>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-[color:var(--mano-ink)] tracking-tight">
          Understanding yourself is only the beginning.
        </h2>
        <p className="mt-5 text-[color:var(--mano-ink-soft)]">
          When you want support, ManoVāk helps you find a next step that feels right for you.
        </p>
      </div>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((c) => (
          <div
            key={c.title}
            data-testid={`beyond-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
            className="relative rounded-3xl border border-[color:var(--mano-border)] bg-white p-6 flex flex-col gap-3 hover:-translate-y-1 transition-transform"
          >
            <div className="flex items-center justify-between">
              <div className="h-11 w-11 rounded-2xl bg-[color:var(--mano-blue-light)] flex items-center justify-center text-[color:var(--mano-primary)]">
                <c.Icon size={18} />
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${
                c.pill === "Live"
                  ? "bg-[color:var(--mano-normal)]/15 text-[color:var(--mano-normal)]"
                  : "bg-[color:var(--mano-blue-light)] text-[color:var(--mano-primary)]"
              }`}>
                {c.pill}
              </span>
            </div>
            <h3 className="font-display text-xl text-[color:var(--mano-ink)]">{c.title}</h3>
            <p className="text-sm text-[color:var(--mano-ink-soft)] leading-relaxed">{c.body}</p>
            <Link to="/register" className="mt-1 inline-flex items-center gap-1 font-semibold text-sm text-[color:var(--mano-primary)]">
              {c.cta} <ArrowRight size={13} />
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}

function HumanSupport() {
  return (
    <section id="experts" className="bg-[color:var(--mano-surface)] py-24">
      <div className={`${container} grid lg:grid-cols-2 gap-12 items-center`}>
        <div>
          <Overline>When you need more</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-[color:var(--mano-ink)] tracking-tight">
            Technology for insight.
            <span className="block text-[color:var(--mano-primary)]">People for support.</span>
          </h2>
          <p className="mt-5 text-[color:var(--mano-ink-soft)] max-w-md">
            ManoVāk can help you check in with yourself, but some moments call for a
            conversation. Connect with qualified professionals when you want additional guidance.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <PrimaryCta to="/register" data-testid="human-support-cta">Find an expert</PrimaryCta>
            <GhostCta to="#how" icon={Video} data-testid="human-support-how">How consultations work</GhostCta>
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6">
          <div className="flex items-center justify-between text-[11px] text-[color:var(--mano-ink-soft)]">
            <span className="uppercase tracking-widest font-semibold">Video consultation</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--mano-normal)]/15 text-[color:var(--mano-normal)] px-2 py-0.5 font-bold uppercase tracking-widest">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--mano-normal)]" /> Live
            </span>
          </div>
          <div className="mt-5 flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-[color:var(--mano-ink)] text-white font-bold flex items-center justify-center text-lg">
              AM
            </div>
            <div>
              <div className="font-display text-xl text-[color:var(--mano-ink)]">Dr. Ananya Mehta</div>
              <div className="text-sm text-[color:var(--mano-ink-soft)]">Psychiatrist · 12 yrs experience</div>
            </div>
          </div>
          <div className="mt-6 flex items-center justify-between rounded-2xl bg-[color:var(--mano-blue-light)]/60 px-4 py-3">
            <span className="text-sm text-[color:var(--mano-ink-soft)]">Tomorrow</span>
            <span className="text-lg font-semibold text-[color:var(--mano-ink)]">4:30 PM</span>
          </div>
          <p className="mt-6 text-sm text-[color:var(--mano-ink-soft)]">
            <b className="text-[color:var(--mano-ink)]">Human support, when it helps.</b> Your screenings
            can inform the conversation — not replace it.
          </p>
        </div>
      </div>
    </section>
  );
}

function Privacy() {
  const cards = [
    { title: "You stay in control", body: "Clear consent around assessments and wellbeing information, with choices that remain understandable.", Icon: ShieldCheck },
    { title: "Designed for privacy", body: "Your information is handled with appropriate safeguards throughout the wellbeing journey.", Icon: Lock },
    { title: "Screening, not diagnosis", body: "ManoVāk supports wellbeing screening and informed next steps; it does not replace professional diagnosis or emergency care.", Icon: Sparkles },
  ];
  return (
    <section className={`${container} py-24`}>
      <Overline>Privacy &amp; safety</Overline>
      <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-[color:var(--mano-ink)] tracking-tight max-w-3xl">
        Your voice is personal. We treat it that way.
      </h2>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {cards.map((c) => (
          <div key={c.title} data-testid={`privacy-${c.title.toLowerCase().replace(/[,\s]+/g, "-")}`} className="rounded-3xl border border-[color:var(--mano-border)] bg-white p-6 flex flex-col gap-3">
            <div className="h-11 w-11 rounded-2xl bg-[color:var(--mano-blue-light)] flex items-center justify-center text-[color:var(--mano-primary)]">
              <c.Icon size={18} />
            </div>
            <h3 className="font-display text-lg text-[color:var(--mano-ink)]">{c.title}</h3>
            <p className="text-sm text-[color:var(--mano-ink-soft)] leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
      <p className="mt-8 text-xs text-[color:var(--mano-ink-soft)] max-w-2xl">
        For emergencies or immediate risk of harm, contact local emergency services or a
        qualified healthcare professional.
      </p>
    </section>
  );
}

function FinalCta() {
  return (
    <section id="final-cta" className="bg-[color:var(--mano-hero)] py-24 text-white">
      <div className={`${container} text-center max-w-3xl mx-auto`}>
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white/60">Your next check-in</div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight tracking-tight">
          You don&rsquo;t always need the right words.
          <span className="block text-white/70">Sometimes, you can just start by speaking.</span>
        </h2>
        <p className="mt-5 text-white/70">
          Take a short ManoVāk voice check-in and begin understanding how you&rsquo;re doing.
        </p>
        <div className="mt-8 inline-flex items-center gap-4 flex-wrap justify-center">
          <Link
            to="/register"
            data-testid="final-cta-btn"
            className="inline-flex items-center gap-2 rounded-full bg-white text-[color:var(--mano-ink)] px-7 py-3.5 font-semibold text-sm hover:-translate-y-0.5 transition-transform"
          >
            Start my voice check-in <ArrowRight size={15} />
          </Link>
          <span className="text-sm text-white/60">Takes only a few minutes</span>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[color:var(--mano-border)] bg-white">
      <div className={`${container} py-14 grid md:grid-cols-3 gap-8 items-start`}>
        <div>
          <BrandBadge />
          <p className="mt-4 text-sm text-[color:var(--mano-ink-soft)] max-w-xs">
            Voice-led screening for mental and cognitive wellbeing. Support that begins with a
            few spoken words.
          </p>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-[color:var(--mano-ink-soft)]">
          <a href="#how" className="hover:text-[color:var(--mano-primary)]">How it works</a>
          <a href="#assessments" className="hover:text-[color:var(--mano-primary)]">Assessments</a>
          <a href="#progress" className="hover:text-[color:var(--mano-primary)]">Progress</a>
          <a href="#care" className="hover:text-[color:var(--mano-primary)]">Care</a>
          <a href="#experts" className="hover:text-[color:var(--mano-primary)]">Experts</a>
        </div>
        <div className="md:text-right">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-[color:var(--mano-ink-soft)]">Follow us</div>
          <div className="mt-3 flex md:justify-end">
            <SocialLinks />
          </div>
          <div className="mt-6 text-[11px] text-[color:var(--mano-ink-soft)]" data-testid="landing-version">
            v1.6.3
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[color:var(--mano-bg)]">
      <Nav />
      <Hero />
      <ValueProps />
      <HowItWorks />
      <TwoWaysToCheckIn />
      <VoiceLab />
      <Journey />
      <BeyondScreening />
      <HumanSupport />
      <Privacy />
      <FinalCta />
      <Footer />
    </div>
  );
}
